import React, { useState, useRef } from 'react';
import { X, Upload, Send, Briefcase, User, Mail, Phone, FileText, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '@/lib/firebase';
import { MONKEY_FRANCHISE_ID } from '@/lib/config';
import toast from 'react-hot-toast';

interface JobApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const JobApplicationModal: React.FC<JobApplicationModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    message: '',
  });
  const [cvFile, setCvFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadClick = () => {
    console.log('Upload button clicked');
    fileInputRef.current?.click();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('=== handleFileChange triggered ===');
    console.log('Event:', e);
    console.log('Files:', e.target.files);

    const file = e.target.files?.[0];
    console.log('Selected file:', file);

    if (file) {
      console.log('File details:', file.name, 'Type:', file.type, 'Size:', file.size);

      // Validate file extension (more reliable than MIME type)
      const fileName = file.name.toLowerCase();
      const allowedExtensions = ['.pdf', '.doc', '.docx'];
      const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

      if (!hasValidExtension) {
        console.log('Invalid extension');
        toast.error(t('jobApplication.invalidFileType', 'Solo se permiten archivos PDF o Word (.pdf, .doc, .docx)'));
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        console.log('File too large');
        toast.error(t('jobApplication.fileTooLarge', 'El archivo no puede superar 5MB'));
        return;
      }

      console.log('Setting cvFile state');
      setCvFile(file);
      toast.success(`Archivo seleccionado: ${file.name}`);
    } else {
      console.log('No file in event');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.fullName.trim() || !formData.email.trim() || !formData.phone.trim()) {
      toast.error(t('jobApplication.requiredFields', 'Por favor completa todos los campos obligatorios'));
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error(t('jobApplication.invalidEmail', 'Por favor introduce un email válido'));
      return;
    }

    setLoading(true);

    try {
      let cvUrl = '';
      let cvFileName = '';

      // Upload CV if provided
      if (cvFile) {
        const timestamp = Date.now();
        const sanitizedFileName = cvFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const storagePath = `job_applications/${MONKEY_FRANCHISE_ID}/${timestamp}_${sanitizedFileName}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, cvFile);
        cvUrl = await getDownloadURL(storageRef);
        cvFileName = cvFile.name;
      }

      // Save to Firestore
      await addDoc(collection(db, 'job_applications'), {
        franchiseId: MONKEY_FRANCHISE_ID,
        fullName: formData.fullName.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        message: formData.message.trim() || null,
        cvUrl: cvUrl || null,
        cvFileName: cvFileName || null,
        status: 'pending',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      toast.success(t('jobApplication.success', 'Solicitud enviada correctamente. Te contactaremos pronto.'));

      // Reset form
      setFormData({ fullName: '', email: '', phone: '', message: '' });
      setCvFile(null);
      onClose();
    } catch (error) {
      console.error('Error submitting job application:', error);
      toast.error(t('jobApplication.error', 'Error al enviar la solicitud. Inténtalo de nuevo.'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="job-application-modal-title"
        className="bg-surface rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Briefcase className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 id="job-application-modal-title" className="text-xl font-bold text-white">
                  {t('jobApplication.title', 'Trabaja con Nosotros')}
                </h2>
                <p className="text-white/80 text-sm">
                  {t('jobApplication.subtitle', 'Únete al equipo Monkey Barber')}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              aria-label={t('common.close', 'Cerrar')}
              className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Full Name */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-ink-muted mb-2">
              <User className="w-4 h-4" />
              {t('jobApplication.fullName', 'Nombre Completo')} *
            </label>
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              placeholder={t('jobApplication.fullNamePlaceholder', 'Tu nombre y apellidos')}
              className="w-full px-4 py-3 border border-hairline rounded-xl bg-surface text-ink focus:ring-2 focus:ring-accent focus:border-transparent transition"
              required
            />
          </div>

          {/* Email */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-ink-muted mb-2">
              <Mail className="w-4 h-4" />
              {t('jobApplication.email', 'Email')} *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder={t('jobApplication.emailPlaceholder', 'tu@email.com')}
              className="w-full px-4 py-3 border border-hairline rounded-xl bg-surface text-ink focus:ring-2 focus:ring-accent focus:border-transparent transition"
              required
            />
          </div>

          {/* Phone */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-ink-muted mb-2">
              <Phone className="w-4 h-4" />
              {t('jobApplication.phone', 'Teléfono')} *
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder={t('jobApplication.phonePlaceholder', '+34 600 000 000')}
              className="w-full px-4 py-3 border border-hairline rounded-xl bg-surface text-ink focus:ring-2 focus:ring-accent focus:border-transparent transition"
              required
            />
          </div>

          {/* CV Upload (Optional) */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-ink-muted mb-2">
              <FileText className="w-4 h-4" />
              {t('jobApplication.cv', 'Curriculum (Opcional)')}
            </label>
            <div className="relative">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleFileChange}
                className="hidden"
              />
              <button
                type="button"
                onClick={handleUploadClick}
                className="flex items-center justify-center gap-2 w-full px-4 py-3 border-2 border-dashed border-hairline rounded-xl bg-surface-2 text-ink-muted hover:border-accent/30 hover:text-accent cursor-pointer transition"
              >
                <Upload className="w-5 h-5" />
                {cvFile ? cvFile.name : t('jobApplication.uploadCV', 'Subir CV (PDF o Word, máx 5MB)')}
              </button>
            </div>
            {cvFile && (
              <button
                type="button"
                onClick={() => setCvFile(null)}
                className="mt-2 text-sm text-danger hover:text-danger"
              >
                {t('jobApplication.removeFile', 'Eliminar archivo')}
              </button>
            )}
          </div>

          {/* Message (Optional) */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-ink-muted mb-2">
              <FileText className="w-4 h-4" />
              {t('jobApplication.message', 'Mensaje (Opcional)')}
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder={t('jobApplication.messagePlaceholder', 'Cuéntanos sobre tu experiencia...')}
              rows={4}
              className="w-full px-4 py-3 border border-hairline rounded-xl bg-surface text-ink focus:ring-2 focus:ring-accent focus:border-transparent transition resize-none"
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 px-6 py-4 text-white font-bold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition shadow-lg"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {t('jobApplication.sending', 'Enviando...')}
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                {t('jobApplication.submit', 'Enviar Solicitud')}
              </>
            )}
          </button>

          <p className="text-center text-xs text-ink-subtle">
            {t('jobApplication.privacy', 'Al enviar este formulario, aceptas que procesemos tus datos para evaluar tu candidatura.')}
          </p>
        </form>
      </div>
    </div>
  );
};

export default JobApplicationModal;
